import React from "react";
import { useNavigate } from "react-router-dom";
import { rupee, imgSrc, placeholderFor } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Plus, Minus, MapPinOff } from "lucide-react";
import { toast } from "sonner";

export default function MasterProductCard({ item }) {
  const nav = useNavigate();
  const { qtyOf, addItem, inc, dec } = useCart();
  const offer = item.offer;
  const qty = offer ? qtyOf(offer.product_id) : 0;
  const disc = offer?.discount_pct || 0;

  const goDetail = () => { if (offer) nav(`/product/${offer.product_id}`); };
  const handleAdd = (e) => {
    e.stopPropagation();
    if (!offer) return;
    const res = addItem({ id: offer.product_id, name: item.name, price: offer.price, mrp: offer.mrp || item.mrp, image: item.image, unit: item.pack_size || item.unit },
      { id: offer.store_id, name: offer.store_name, delivery_fee: offer.delivery_fee });
    if (!res.conflict) toast.success(`${item.name} added to cart`);
  };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div data-testid="master-product-card" onClick={goDetail}
      className={`group relative flex flex-col justify-between rounded-2xl border border-slate-200/80 bg-white p-3 transition-all duration-200 ${offer ? "cursor-pointer hover:border-brand/40 hover:shadow-md" : ""}`}>
      {disc > 0 && <span className="absolute left-2 top-2 z-10 rounded-md bg-amber-surge px-1.5 py-0.5 text-[10px] font-bold text-white">{disc}% OFF</span>}
      {item.featured && !disc && <span className="absolute left-2 top-2 z-10 rounded-md bg-brand px-1.5 py-0.5 text-[10px] font-bold text-white">Featured</span>}
      <div className="overflow-hidden rounded-xl bg-slate-50">
        <img src={imgSrc(item.image, item.category)} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderFor(item.category); }} alt={item.name} loading="lazy" className={`h-28 w-full object-cover transition-transform duration-300 ${offer ? "group-hover:scale-105" : "opacity-70"}`} />
      </div>
      <div className="mt-2 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{item.brand || "\u00a0"}{item.pack_size ? ` · ${item.pack_size}` : ""}</p>
        <h4 className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-slate-800 group-hover:text-brand">{item.name}</h4>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        {offer ? (
          <div>
            <span data-testid="master-price" className="font-mono text-sm font-bold text-slate-900">{rupee(offer.price)}</span>
            {offer.mrp > offer.price && <span className="ml-1 font-mono text-xs text-slate-400 line-through">{rupee(offer.mrp)}</span>}
            <p className="text-[10px] text-slate-400">{offer.store_name}{offer.distance_km != null ? ` · ${offer.distance_km} km` : ""}{item.offer_count > 1 ? ` · +${item.offer_count - 1} more` : ""}</p>
          </div>
        ) : (
          <div>
            <span className="font-mono text-sm font-bold text-slate-500">MRP {rupee(item.mrp)}</span>
            <p data-testid="master-unavailable" className="flex items-center gap-1 text-[10px] font-semibold text-slate-400"><MapPinOff className="h-3 w-3" /> Not available near you</p>
          </div>
        )}
        {offer && (qty === 0 ? (
          <button data-testid="master-add-btn" onClick={handleAdd} className="rounded-lg border border-brand/30 bg-brand-soft px-3 py-1.5 text-xs font-bold text-brand transition-all hover:bg-brand hover:text-white active:scale-90">ADD</button>
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-brand px-1.5 py-1 text-white" onClick={(e) => e.stopPropagation()}>
            <button onClick={stop(() => dec(offer.product_id))}><Minus className="h-4 w-4" /></button>
            <span className="min-w-[18px] text-center text-sm font-bold">{qty}</span>
            <button onClick={stop(() => inc(offer.product_id))}><Plus className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
